/**
 * 3D Asset Library — Super Admin
 *
 * Lists every 3D asset ever generated or uploaded (the global `meshy_assets`
 * collection), across every lesson, with cross-lesson usage visibility.
 * Studio's Assets tab already lets an author browse this same collection and
 * link an existing asset into the lesson they're editing (AssetLibraryPicker);
 * this screen is the read-heavy, superadmin-only overview of the whole library.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { AssetManager } from '../../services/assets/assetManager';
import {
  AlertCircle,
  AlertTriangle,
  Box,
  Check,
  Copy,
  Download,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperadmin } from '../../utils/rbac';
import { formatAssetDate } from '../../utils/relativeTime';
import { AssetRecoveryPanel } from '../../Components/admin/AssetRecoveryPanel';

interface LibraryAsset {
  id: string;
  name?: string;
  prompt?: string;
  status?: string;
  thumbnail_url?: string;
  glb_url?: string;
  fbx_url?: string;
  usdz_url?: string;
  meshy_id?: string;
  chapter_id?: string;
  topic_id?: string;
  isCore?: boolean;
  assetTier?: string;
  linked_topics?: string[];
  created_at?: unknown;
  metadata?: { source?: string };
}

const PAGE_SIZE = 48;

export default function AssetLibraryAdmin() {
  const { user, profile, profileLoading } = useAuth();

  const [assets, setAssets] = useState<LibraryAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | string>('all');
  const [tierFilter, setTierFilter] = useState<'all' | 'core' | 'non-core'>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LibraryAsset | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canView = Boolean(profile && isSuperadmin(profile));

  const fetchAssets = async (loadMore = false) => {
    setLoading(true);
    setError(null);
    try {
      let q = query(collection(db, 'meshy_assets'), orderBy('created_at', 'desc'), limit(PAGE_SIZE));
      if (loadMore && lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      const snap = await getDocs(q);
      const page = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<LibraryAsset, 'id'>) }));
      setAssets((prev) => (loadMore ? [...prev, ...page] : page));
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err: any) {
      console.error('[AssetLibraryAdmin] Failed to load assets:', err);
      setError(err?.message || 'Failed to load 3D assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  if (profileLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-border border-t-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-24">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Only super admins can view the 3D asset library.</p>
        </div>
      </div>
    );
  }

  const filtered = assets.filter((a) => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (tierFilter === 'core' && !(a.isCore || a.assetTier === 'core')) return false;
    if (tierFilter === 'non-core' && (a.isCore || a.assetTier === 'core')) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (a.name || '').toLowerCase().includes(q) || (a.prompt || '').toLowerCase().includes(q);
  });

  const copyId = (id: string) => {
    navigator.clipboard?.writeText(id).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget || !user?.uid) return;
    setDeletingId(deleteTarget.id);
    try {
      // Reuse the same delete path as the Studio Assets tab when we know an
      // origin lesson (cleans up the Storage file and unlinks that topic);
      // otherwise just remove the orphaned Firestore doc directly.
      if (deleteTarget.chapter_id && deleteTarget.topic_id) {
        const result = await AssetManager.deleteAsset(
          { assetId: deleteTarget.id, chapterId: deleteTarget.chapter_id, topicId: deleteTarget.topic_id, userId: user.uid },
          profile,
          { isCore: deleteTarget.isCore, assetTier: deleteTarget.assetTier, glb_url: deleteTarget.glb_url }
        );
        if (!result.success) throw new Error(result.error || 'Failed to delete asset');
      } else {
        await deleteDoc(doc(db, 'meshy_assets', deleteTarget.id));
      }
      setAssets((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      toast.success('Asset deleted');
    } catch (err: any) {
      console.error('[AssetLibraryAdmin] Failed to delete asset:', err);
      toast.error(err?.message || 'Failed to delete asset');
    } finally {
      setDeletingId(null);
      setDeleteTarget(null);
    }
  };

  const usageCount = (a: LibraryAsset) => a.linked_topics?.length ?? (a.chapter_id && a.topic_id ? 1 : 0);

  return (
    <div className="min-h-screen bg-background pt-24 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">3D Asset Library</h1>
          <p className="text-muted-foreground">
            Every 3D asset generated or uploaded across all lessons. Authors can add any of these to a
            lesson from the Assets tab's "Browse Library" option.
          </p>
        </div>

        <AssetRecoveryPanel onChanged={() => fetchAssets()} />

        {/* Filters */}
        <div className="bg-card rounded-lg border border-border p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name or prompt..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All statuses</option>
              <option value="complete">Complete</option>
              <option value="ready">Ready</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value as typeof tierFilter)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All tiers</option>
              <option value="core">Core only</option>
              <option value="non-core">Non-core only</option>
            </select>
            <button
              onClick={() => fetchAssets()}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {loading && assets.length === 0 ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading 3D assets...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Box className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No assets found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {filtered.map((asset) => {
              const isCore = asset.isCore || asset.assetTier === 'core';
              return (
                <div key={asset.id} className="bg-card rounded-lg border border-border p-3 flex flex-col">
                  <div className="aspect-square rounded-md bg-muted overflow-hidden mb-2 relative">
                    {asset.thumbnail_url ? (
                      <img src={asset.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Box className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    {isCore && (
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-amber-500/90 text-amber-950 text-[9px] font-semibold">
                        Core
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium text-foreground truncate" title={asset.name}>
                    {asset.name || 'Untitled asset'}
                  </p>
                  <div className="flex items-center justify-between mt-1 mb-2">
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        asset.status === 'complete' || asset.status === 'ready'
                          ? 'text-emerald-400 bg-emerald-500/10'
                          : asset.status === 'failed'
                            ? 'text-red-400 bg-red-500/10'
                            : 'text-amber-400 bg-amber-500/10'
                      }`}
                    >
                      {asset.status || 'unknown'}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {usageCount(asset)} lesson{usageCount(asset) === 1 ? '' : 's'}
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mb-2">{formatAssetDate(asset.created_at)}</p>

                  <div className="mt-auto flex items-center gap-1 flex-wrap">
                    <button
                      onClick={() => copyId(asset.id)}
                      title="Copy asset ID"
                      className="p-1.5 rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                    >
                      {copiedId === asset.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    {asset.glb_url && (
                      <a
                        href={asset.glb_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Download GLB"
                        className="p-1.5 rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {asset.glb_url && (
                      <a
                        href={asset.glb_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open GLB in new tab"
                        className="p-1.5 rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      onClick={() => setDeleteTarget(asset)}
                      title="Delete asset"
                      className="p-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 ml-auto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {hasMore && filtered.length > 0 && (
          <div className="text-center pt-6">
            <button
              onClick={() => fetchAssets(true)}
              disabled={loading}
              className="px-6 py-3 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">Delete "{deleteTarget.name || 'this asset'}"?</h3>
              <p className="text-muted-foreground mb-2">This permanently removes the asset. This action cannot be undone.</p>
              {usageCount(deleteTarget) > 1 && (
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-left">
                  <p className="text-xs text-amber-400 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      This asset is linked to {usageCount(deleteTarget)} lessons. Deleting it removes the
                      model globally — other lessons that reference it may be left with a broken asset.
                    </span>
                  </p>
                </div>
              )}
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="px-6 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-slate-700 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deletingId !== null}
                  className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-foreground bg-red-600 hover:bg-red-500 rounded-lg transition-all disabled:opacity-50"
                >
                  {deletingId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
